import {
  Box,
  Card,
  Checkbox,
  Divider,
  ExclamationCircleIcon,
  Flex,
  Heading,
  Input,
  Label,
  Layout,
  ListItem,
  Text,
  ValidationMessage,
} from '@modusign/design-system';
import { useSigner } from '@modusign/react-signing';
import { ChangeEvent, SetStateAction, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';

import noop from '@libs/noop';

import LimitedTextarea from '@app/components/LimitedTextarea';
import { contractLinkCreateMaxSigningPerParticipantChange } from '@app/features/contractLink/contractLinkCreate';
import { validateTitle } from '@app/libs/validation';

interface ContractLinkCreateInfoCardProps {
  allowDuplicatedSign: {
    value: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    toggle: (nextValue?: any) => void;
  };
  maxSigningPerParticipant: {
    value: number | null;
    limit: number;
    error: string;
    setError: React.Dispatch<SetStateAction<string>>;
  };
  messageInput: {
    isOpen: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    toggle: (nextValue?: any) => void;
  };
  title: {
    value: string | null;
    error: string;
    onChangeValue: (v: string) => void;
    setError: React.Dispatch<SetStateAction<string>>;
  };
  signerId: string;
}

function ContractLinkCreateInfoCard({
  allowDuplicatedSign,
  maxSigningPerParticipant,
  messageInput,
  title,
  signerId,
}: ContractLinkCreateInfoCardProps) {
  const dispatch = useDispatch();
  const { setMessage, signer } = useSigner(signerId);

  const handleAllowDuplicatedSignToggle = useCallback(() => {
    if (allowDuplicatedSign.value && maxSigningPerParticipant.value) {
      dispatch(
        contractLinkCreateMaxSigningPerParticipantChange({ maxSigningPerParticipant: null })
      );
    }
    allowDuplicatedSign.toggle();
    if (allowDuplicatedSign.value) {
      maxSigningPerParticipant.setError('');
      dispatch(
        contractLinkCreateMaxSigningPerParticipantChange({
          maxSigningPerParticipant: null,
        })
      );
    }
  }, [dispatch, allowDuplicatedSign, maxSigningPerParticipant]);

  const maxSigningPerParticipantDigitMaxLength = maxSigningPerParticipant.limit.toString().length;
  const validateMaxSigningPerParticipant = useCallback(
    (value: number | null) => {
      const hasError = !value || value < 2 || value > maxSigningPerParticipant.limit;
      return hasError ? `2건 이상 ${maxSigningPerParticipant.limit}건 이하로 입력해 주세요.` : '';
    },
    [maxSigningPerParticipant]
  );
  const handleMaxSigningPerParticipantChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const numberValue = Number(value);
      dispatch(
        contractLinkCreateMaxSigningPerParticipantChange({ maxSigningPerParticipant: numberValue })
      );

      maxSigningPerParticipant.setError(validateMaxSigningPerParticipant(numberValue));
    },
    [dispatch, maxSigningPerParticipant, validateMaxSigningPerParticipant]
  );

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const { message } = validateTitle(value, { maxLength: 50, name: '링크 이름' });
      title.setError(message);
      title.onChangeValue(value);
    },
    [title]
  );

  useMount(() => {
    const { message } = validateTitle(title.value || '', { maxLength: 50, name: '링크 이름' });
    title.setError(message);
    maxSigningPerParticipant.setError(
      maxSigningPerParticipant.value
        ? validateMaxSigningPerParticipant(maxSigningPerParticipant.value)
        : ''
    );
    allowDuplicatedSign.toggle(!!maxSigningPerParticipant.value);
  });

  return (
    <>
      <Layout
        alignItems="center"
        bg="#d2d2d2"
        borderRadius={4}
        justifyContent="center"
        mb={4}
        pt={4}
        px={4}
        width="100%"
      >
        <Card flex={1} mb={4} p={0} pb={8} pt={7} px={7}>
          <Heading fontSize="md" mb={6}>
            링크 설정
          </Heading>
          <Box as="ul">
            <Box mb={5} width="100%">
              <ListItem>
                <Label fontSize={1} htmlFor="linkTitle" mb={4}>
                  링크 이름
                </Label>
              </ListItem>
              <Box pl={4}>
                <Input
                  id="linkTitle"
                  isInvalid={!!title.error}
                  maxLength={51}
                  name="linkTitle"
                  placeholder="링크 이름을 50자 이내로 입력해주세요."
                  value={title.value ?? ''}
                  onChange={handleTitleChange}
                />
                <ValidationMessage>{title.error}</ValidationMessage>
              </Box>
            </Box>
            <Divider color="light" mb={5} />
            <ListItem>
              <Flex justifyContent="space-between" width="100%">
                <Label htmlFor="allowDuplicatedSign">중복 서명 허용</Label>
                <Checkbox
                  checked={allowDuplicatedSign.value}
                  id="allowDuplicatedSign"
                  onChange={noop}
                  onClick={handleAllowDuplicatedSignToggle}
                />
              </Flex>
              {allowDuplicatedSign.value && (
                <Box>
                  <Text as="p" color="textNeutralLight" mb={4}>
                    동일한 서명자가 여러번 서명할 수 있도록 합니다.
                    <br />
                    1인당 서명을 완료할 수 있는 최대 건수를 입력해 주세요.
                  </Text>
                  <Box mb={4}>
                    <Box mb={1} sx={{ position: 'relative' }} width="100%">
                      <Input
                        aria-label="참여자당 서명 허용 건수"
                        defaultValue={maxSigningPerParticipant.value ?? ''}
                        id="maxSigningPerParticipant"
                        isInvalid={!!maxSigningPerParticipant.error}
                        maxLength={maxSigningPerParticipantDigitMaxLength}
                        name="maxSigningPerParticipant"
                        placeholder={`2 ~ ${maxSigningPerParticipant.limit}`}
                        sx={{ paddingRight: 8 }}
                        onChange={handleMaxSigningPerParticipantChange}
                      />
                      <Text lineHeight="1.3em" sx={{ position: 'absolute', top: 3, right: 4 }}>
                        건
                      </Text>
                    </Box>
                    <ValidationMessage>{maxSigningPerParticipant.error}</ValidationMessage>
                  </Box>
                  <Flex
                    alignItems="center"
                    bg="notificationBackgroundPrimaryBase"
                    border="1px solid"
                    borderColor="notificationBorderPrimaryBase"
                    borderRadius="3px"
                    px={5}
                    py={4}
                  >
                    <Box as="span" color="iconPrimaryDark">
                      <ExclamationCircleIcon height={13} width={13} />
                    </Box>
                    <Text color="textNeutralSlightlyLight" fontSize="sm" ml={2}>
                      링크 생성 이후 변경이 불가능하니 잘 확인해 주세요.
                    </Text>
                  </Flex>
                </Box>
              )}
            </ListItem>
            <Divider color="light" my={5} />
            <ListItem>
              <Flex justifyContent="space-between" width="100%">
                <Label htmlFor="toggleMessageInput">남길 말 입력</Label>
                <Checkbox
                  checked={messageInput.isOpen}
                  id="toggleMessageInput"
                  onClick={messageInput.toggle}
                />
              </Flex>
              {messageInput.isOpen && (
                <LimitedTextarea
                  bg="formBackgroundNeutralSlightlyDark"
                  height="100px"
                  mt={4}
                  placeholder="남길 말을 입력해 주세요."
                  value={signer?.message ?? ''}
                  onChange={(message) => {
                    setMessage(message);
                  }}
                />
              )}
            </ListItem>
          </Box>
        </Card>
      </Layout>
    </>
  );
}

export default ContractLinkCreateInfoCard;
